import { Optional, Pipe, PipeTransform } from '@angular/core';

import { EnvironmentService } from '../environment/environment.service';
import { isString } from '../helpers';

import { DateTransformInput, DateTransformOptions } from './date-format-types';
import {
  appendTimezoneToValue,
  convertTimezoneName,
  convertTimezoneToLocal,
  convertToLocaleDate,
  formatDatePart,
  formatTimePart,
  splitFormatPattern,
  toDate
} from './date-format.helper';

/**
 * @description
 *
 * This is a simplified date format pipe which does only support numeric formats.
 *
 * Most of the logic has been taken from the original Angular DatePipe and altered
 * where required to reduce it to just numeric date-time values.
 *
 * The originals this file is based on can be found here:
 * https://raw.githubusercontent.com/angular/angular/8.2.x/packages/common/src/pipes/date_pipe.ts
 * https://raw.githubusercontent.com/angular/angular/8.2.x/packages/common/src/i18n/format_date.ts
 *
 *
 * ### Pattern options
 *
 * You can construct a format string using symbols to specify the components
 * of a date-time value, as described in the following table.
 *
 *  | Field type         | Format      | Description                                                   | Example Value                                              |
 *  |--------------------|-------------|---------------------------------------------------------------|------------------------------------------------------------|
 *  | Year               | y           | Numeric: minimum digits                                       | 2, 20, 201, 2017, 20173                                    |
 *  |                    | yy          | Numeric: 2 digits + zero padded                               | 02, 20, 01, 17, 73                                         |
 *  |                    | yyy         | Numeric: 3 digits + zero padded                               | 002, 020, 201, 2017, 20173                                 |
 *  |                    | yyyy        | Numeric: 4 digits or more + zero padded                       | 0002, 0020, 0201, 2017, 20173                              |
 *  | Month              | M           | Numeric: 1 digit                                              | 9, 12                                                      |
 *  |                    | MM          | Numeric: 2 digits + zero padded                               | 09, 12                                                     |
 *  | Month standalone   | L           | Numeric: 1 digit                                              | 9, 12                                                      |
 *  |                    | LL          | Numeric: 2 digits + zero padded                               | 09, 12                                                     |
 *  | Week of year       | w           | Numeric: minimum digits                                       | 1... 53                                                    |
 *  |                    | ww          | Numeric: 2 digits + zero padded                               | 01... 53                                                   |
 *  | Week of month      | W           | Numeric: 1 digit                                              | 1... 5                                                     |
 *  | Day of month       | d           | Numeric: minimum digits                                       | 1                                                          |
 *  |                    | dd          | Numeric: 2 digits + zero padded                               | 01                                                          |
 *  | Hour 1-12          | h           | Numeric: minimum digits                                       | 1, 12                                                      |
 *  |                    | hh          | Numeric: 2 digits + zero padded                               | 01, 12                                                     |
 *  | Hour 0-23          | H           | Numeric: minimum digits                                       | 0, 23                                                      |
 *  |                    | HH          | Numeric: 2 digits + zero padded                               | 00, 23                                                     |
 *  | Minute             | m           | Numeric: minimum digits                                       | 8, 59                                                      |
 *  |                    | mm          | Numeric: 2 digits + zero padded                               | 08, 59                                                     |
 *  | Second             | s           | Numeric: minimum digits                                       | 0... 59                                                    |
 *  |                    | ss          | Numeric: 2 digits + zero padded                               | 00... 59                                                   |
 *  | Fractional seconds | S           | Numeric: 1 digit                                              | 0... 9                                                     |
 *  |                    | SS          | Numeric: 2 digits + zero padded                               | 00... 99                                                   |
 *  |                    | SSS         | Numeric: 3 digits + zero padded (= milliseconds)              | 000... 999                                                 |
 *
 * ### Format examples
 *
 * These examples transform a date into various formats,
 * assuming that `dateObj` is a JavaScript `Date` object for
 * year: 2015, month: 6, day: 15, hour: 21, minute: 43, second: 11,
 * given in the local time for the `en-US` locale.
 *
 * ```
 * {{ dateObj | numericDateFormat: 'mm:ss' }}       // output is '43:11'
 * {{ dateObj | numericDateFormat: 'MM/dd/yyyy' }}  // output is '06/15/2015'
 * {{ dateObj | numericDateFormat: { format: 'MM/dd/yyyy', toLocaleDate: true, includeTimezone: true } }}  // output is '06/15/2015'
 * ```
 *
 * Transform options:
 * - format: the format pattern
 * - toLocaleDate: converts the date to the date used by the locale, when it uses a different cultural calendar
 * - includeTimezone: if provided iso date/time value including timezone should include or ignore (default) the timezone offset.
 *                    Ignoring timezone is required if provided time/date includes UTC timezone but pipe additionally adds/subtracts
 *                    browser timezone offset, which finally leads to wrong date calculation.
 * - timezone: a time zone whose offset should be applied to the date (and time).
 *             has no contradiction or interference with includeTimezone
 * - addTime: append the time to formatted date
 * - timezoneSuffix: if `addTime` is set, append a timezone to the formatted date/time
 */
@Pipe({
  name: 'numericDateFormat'
})
export class NumericDateFormatPipe implements PipeTransform {
  private readonly format: string;
  private readonly locale: string;

  constructor(@Optional() protected readonly envService: EnvironmentService) {
    this.format = envService?.dateFormat;
    this.locale = envService?.languageTag;
  }

  /**
   * @param value The date expression: a `Date` object,  a number
   * (milliseconds since UTC epoch), or an ISO string (https://www.w3.org/TR/NOTE-datetime).
   * @param options can be either string for custom format or an object, containing `format`
   * and `toLocaleDate`, in case date should be converted to local date (respecting different cultural calendar)
   * @returns A date string in the desired format.
   */
  transform(value: DateTransformInput, options?: DateTransformOptions | string): string | null {
    if (value == null || value === '') {
      return null;
    }

    const transformOptions = this.prepareTransformOptions(value, options || {});

    return this.formatDate(value, transformOptions);
  }

  private prepareTransformOptions(
    value: DateTransformInput,
    options: DateTransformOptions | string
  ): DateTransformOptions {
    if (isString(options)) {
      return { format: options };
    }

    const { format, toLocaleDate, includeTimezone, timezone, fromTimezone, addTime, timezoneSuffix } = options;
    return {
      format: isString(format) ? format : this.format,
      toLocaleDate: this.locale && toLocaleDate,
      timezone: timezone ? convertTimezoneName(value, timezone) : '',
      includeTimezone,
      fromTimezone,
      addTime,
      timezoneSuffix
    };
  }

  /**
   * @description
   *
   * Formats a date according to the pattern passed.
   *
   * @param value The date to format, as a Date, or a number (milliseconds since UTC epoch)
   * or an [ISO date-time string](https://www.w3.org/TR/NOTE-datetime).
   * @param options DateTransformOptions
   * @returns The formatted date string.
   *
   *
   * Taken from https://raw.githubusercontent.com/angular/angular/8.2.x/packages/common/src/i18n/format_date.ts
   * and altered.
   *
   * Difference to DatePipe:
   * - Renamed variables to show connections and remove shadowing issue from original DatePipe.
   * - include / exclude timezone offset
   * - optional time formatted by JS
   */
  private formatDate(
    value: DateTransformInput,
    {
      format,
      timezone = '',
      fromTimezone = '',
      toLocaleDate = false,
      includeTimezone = false,
      addTime = false,
      timezoneSuffix = ''
    }: DateTransformOptions
  ): string {
    if (fromTimezone) {
      value = appendTimezoneToValue(value, fromTimezone);
      includeTimezone = true;
    }

    // Difference to DatePipe: Add transform based on locale.
    let date = toLocaleDate
      ? convertToLocaleDate(toDate(value, includeTimezone), this.locale)
      : toDate(value, includeTimezone);

    // Difference to DatePipe: partially removed timezone offset handling.
    if (timezone) {
      date = convertTimezoneToLocal(date, timezone, true, isString(value) && value.slice(-1) === 'Z');
    }

    const formattedDate = splitFormatPattern(format)
      .map(p => formatDatePart(p, date))
      .join('');

    // Difference to DatePipe: Time to be added optionally
    // As Hilti MO date format is lacking time format, time will be formatted by JS
    const formattedTime = addTime ? formatTimePart(date, this.locale, timezoneSuffix, toLocaleDate) : '';
    const separator = formattedDate && addTime ? ' ' : '';

    return formattedDate + separator + formattedTime;
  }
}
